import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling, faJar, faLeaf, faHandshake } from '@fortawesome/free-solid-svg-icons';
// import CategoryNav from '../components/CategoryNav';
import ImkerCard from '../components/ImkerCard';
import { SkeletonGrid } from '../components/SkeletonCard';
import SeoHead from '../components/SeoHead';
import { organisation, zusammen } from '../utils/strukturierteDaten';
import { useQuery } from '@tanstack/react-query';
import { fetchLocations, fetchAllRegions } from '../utils/api';

const HomePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [geoError, setGeoError] = useState('');
    const [geoLoading, setGeoLoading] = useState(false);

    const homeStructuredData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "dein-Imker.de",
        "url": "https://www.dein-imker.de",
        "description": "Finde Imker in deiner Nähe — Honig, Bienenwachs und Bienenprodukte direkt vom lokalen Imker kaufen.",
        "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.dein-imker.de/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
        }
    };

    const handleGeolocation = useCallback(() => {
        if (!navigator.geolocation) {
            setGeoError('Geolocation wird von deinem Browser nicht unterstützt.');
            return;
        }
        setGeoLoading(true);
        setGeoError('');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setGeoLoading(false);
                navigate(`/map?lat=${position.coords.latitude.toFixed(6)}&lng=${position.coords.longitude.toFixed(6)}`);
            },
            (err) => {
                setGeoLoading(false);
                if (err.code === 1) {
                    setGeoError('Standortzugriff wurde abgelehnt. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.');
                } else {
                    setGeoError('Standort konnte nicht ermittelt werden. Bitte versuche es erneut.');
                }
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
    }, [navigate]);

    const {
        data: locationsData,
        isLoading: locationsLoading,
        error: locationsError
    } = useQuery({
        queryKey: ['locations', 'newest', 6],
        queryFn: () => fetchLocations({ sort: 'newest', limit: 6 }),
        staleTime: 1000 * 60 * 30,
        gcTime: 1000 * 60 * 60
    });

    const {
        data: regions = {},
        isLoading: regionsLoading
    } = useQuery({
        queryKey: ['regions'],
        queryFn: fetchAllRegions,
        staleTime: 1000 * 60 * 30,
        gcTime: 1000 * 60 * 60
    });

    const locations = locationsData?.locations || [];
    const isLoading = locationsLoading || regionsLoading;
    const error = locationsError;

    if (isLoading) {
        return (
            <>
                <div style={{ background: 'linear-gradient(135deg, #fff9eb 0%, #fff4d9 50%, #ffefc2 100%)' }}>
                    <Container>
                        <div className="skeleton-pulse rounded my-5" style={{ height: '300px', background: '#e9e5dc' }} />
                    </Container>
                </div>
                <Container className="py-5">
                    <div className="skeleton-pulse rounded mb-4" style={{ height: '28px', width: '250px', background: '#e9e5dc' }} />
                    <Row xs={1} md={2} lg={3} className="g-4">
                        <SkeletonGrid count={6} />
                    </Row>
                </Container>
            </>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <h4 className="text-danger">Fehler beim Laden der Daten</h4>
                    <p className="text-muted">{error.message || String(error)}</p>
                </div>
            </Container>
        );
    }

    return (
        <>
            <SeoHead
                title="Imker in der Nähe finden — Honig direkt vom Imker"
                description="Finde Imker in deiner Nähe. Regionaler Honig, Bienenwachs und Bienenprodukte direkt vom lokalen Imker kaufen — kostenlos auf dein-Imker.de."
                path="/"
                structuredData={zusammen(homeStructuredData, organisation())}
            />
            <div style={{
                background: 'linear-gradient(135deg, #fff9eb 0%, #fff4d9 50%, #ffefc2 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Container>
                    <Row className="align-items-center" style={{ minHeight: '480px' }}>
                        <Col lg={6} className="py-5 text-center text-lg-start">
                            <span className="badge rounded-pill px-3 py-2 mb-3" style={{
                                background: 'rgba(228,179,99,0.2)',
                                color: '#8B6914',
                                fontSize: '13px',
                                fontWeight: '600'
                            }}>
                                <FontAwesomeIcon icon={faSeedling} className="me-1" /> Kostenlos für Imker & Besucher
                            </span>
                            <h1 className="fw-bold mb-3" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.2 }}>
                                {t('home.title')}
                            </h1>
                            <p className="mb-4" style={{ fontSize: '18px', color: '#555', maxWidth: '480px' }}>
                                {t('home.subtitle')}
                            </p>
                            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                                <Link to="/map" className="btn btn-lg px-5 py-3" style={{
                                    background: '#222',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                    fontSize: '16px'
                                }}>
                                    Karte öffnen
                                </Link>
                                <Link to="/category/alle" className="btn btn-lg px-5 py-3" style={{
                                    background: '#fff',
                                    color: '#222',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    border: '2px solid #222'
                                }}>
                                    Alle Imkereien
                                </Link>
                            </div>
                            <div className="mt-3 d-flex flex-column align-items-center align-items-lg-start">
                                <button
                                    onClick={handleGeolocation}
                                    disabled={geoLoading}
                                    className="btn btn-sm px-4 py-2"
                                    style={{
                                        background: 'transparent',
                                        color: '#8B6914',
                                        borderRadius: '10px',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        border: '1px solid #E4B363'
                                    }}
                                >
                                    {geoLoading ? 'Standort wird ermittelt...' : 'Imker in meiner Nähe'}
                                </button>
                                {geoError && (
                                    <small className="text-danger mt-2" style={{ maxWidth: '320px' }}>{geoError}</small>
                                )}
                            </div>
                        </Col>
                        <Col lg={6} className="d-none d-lg-block py-4">
                            <img
                                src="/images/hero-dein-imker.jpg"
                                alt="Imker mit Bienen — dein-Imker.de"
                                className="img-fluid"
                                loading="eager"
                                width={600}
                                height={420}
                                style={{
                                    borderRadius: '20px',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                                    maxHeight: '420px',
                                    objectFit: 'cover',
                                    width: '100%'
                                }}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Benefits Section */}
            <Container className="py-5">
                <div className="text-center mb-5">
                    <h2 className="fw-bold">Warum beim lokalen Imker kaufen?</h2>
                    <p className="text-muted">Drei gute Gründe für Honig aus deiner Region.</p>
                </div>
                <Row className="g-4">
                    {[
                        {
                            icon: faJar,
                            title: 'Qualität & Reinheit',
                            text: 'Naturbelassener Honig direkt vom Imker. Keine industrielle Verarbeitung, keine Zusatzstoffe — nur pure Natur.'
                        },
                        {
                            icon: faLeaf,
                            title: 'Regional & Nachhaltig',
                            text: 'Kurze Transportwege, weniger CO₂ und Unterstützung der lokalen Wirtschaft. Gut für dich und die Umwelt.'
                        },
                        {
                            icon: faHandshake,
                            title: 'Persönlicher Kontakt',
                            text: 'Lerne die Menschen hinter deinem Honig kennen. Erfahre mehr über Bienenhaltung und die Leidenschaft der Imker.'
                        }
                    ].map((benefit, i) => (
                        <Col md={4} key={i}>
                            <div className="text-center p-4 h-100" style={{
                                background: '#fafafa',
                                borderRadius: '16px',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}>
                                <div style={{ fontSize: '48px', marginBottom: '12px', color: '#E4B363' }}><FontAwesomeIcon icon={benefit.icon} /></div>
                                <h3 className="h5 fw-bold mb-2">{benefit.title}</h3>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{benefit.text}</p>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Regions Section */}
            {Object.keys(regions).length > 0 && (
                <div style={{ background: '#fafafa' }}>
                    <Container className="py-5">
                        <div className="text-center mb-4">
                            <h2 className="fw-bold">{t('home.discoverRegions')}</h2>
                            <p className="text-muted">
                                Jede Region hat ihre eigenen Besonderheiten — vom Heidehonig in Niedersachsen
                                bis zum Waldhonig aus dem Schwarzwald.
                            </p>
                        </div>
                        <Row className="g-4">
                            {Object.entries(regions).map(([land, { iso, states }]) => (
                                <Col md={4} key={land}>
                                    <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                                        <Card.Body className="p-4">
                                            <h3 className="h5 fw-bold mb-3">{land}</h3>
                                            <div className="d-flex flex-wrap gap-2">
                                                {states.filter(r => r && r.trim()).slice(0, 8).map(region => (
                                                    <Link
                                                        key={region}
                                                        to={`/imker-in/${iso}/${region.toLowerCase()}`}
                                                        className="text-decoration-none small px-2 py-1"
                                                        style={{
                                                            background: '#f7f1e3',
                                                            color: '#8B6914',
                                                            borderRadius: '6px',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        {region}
                                                    </Link>
                                                ))}
                                                {states.length > 8 && (
                                                    <span className="small px-2 py-1" style={{
                                                        background: '#f0f0f0',
                                                        color: '#717171',
                                                        borderRadius: '6px',
                                                        fontSize: '12px'
                                                    }}>
                                                        + {states.length - 8} mehr
                                                    </span>
                                                )}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </div>
            )}

            {/* New Locations Section */}
            {locations.length > 0 && (
                <Container className="py-5">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="fw-bold mb-0">Neu auf dein-Imker.de</h2>
                        <Link to="/category/alle" className="text-decoration-none fw-600" style={{ color: '#222' }}>
                            Alle anzeigen →
                        </Link>
                    </div>
                    <Row xs={1} md={2} lg={3} className="g-4">
                        {locations.map(location => (
                            <Col key={location.id}>
                                <ImkerCard location={location} />
                            </Col>
                        ))}
                    </Row>
                </Container>
            )}

            {/* CTA for Beekeepers */}
            <div style={{ background: '#222', color: '#fff' }}>
                <Container className="py-5 text-center">
                    <h2 className="fw-bold mb-3">Du bist Imker?</h2>
                    <p className="mb-4" style={{ color: '#aaa', maxWidth: '480px', margin: '0 auto' }}>
                        Präsentiere deine Imkerei kostenlos auf dein-Imker.de und werde von
                        Honigliebhabern in deiner Region gefunden.
                    </p>
                    <Link to="/for-beekeepers" className="btn btn-lg px-5 py-3" style={{
                        background: '#E4B363',
                        color: '#222',
                        borderRadius: '12px',
                        fontWeight: '600'
                    }}>
                        Mehr erfahren
                    </Link>
                </Container>
            </div>
        </>
    );
};

export default HomePage;
